import { Pagination } from 'src/api/common';
import api from './baseApi';

export enum ContentType {
  SCRIPT = 'SCRIPT',
  CIRCLE_PLAN = 'CIRCLE_PLAN',
}

export enum Tone {
  FORMAL = 'FORMAL',
  CASUAL = 'CASUAL',
  NEUTRAL = 'NEUTRAL',
}

export interface GeneratedScriptMetaData {
  topic: string;
  keyPoints: string[];
  coachingGoal: string;
}

export interface GeneratedScript {
  _id: string;
  createdAt: string;
  updatedAt: string;
  user_id: string;
  planId?: string;
  challengeId?: number;
  title: string;
  content: string;
  metaData: GeneratedScriptMetaData;
  isEdited?: boolean;
  deleted?: boolean;
}

export interface GenerateScriptParams {
  topic: string;
  keyPoints: string[];
  coachingGoal: string;
  planId?: string;
  challengeId?: string;
}

export interface Topic {
  topic: string;
  focus: string;
  goal: string;
}

export interface Challenge {
  _id: string;
  title: string;
  challenge: string;
  response: string;
  scriptId?: string;
  postId?: string;
}

export interface GeneratePlanParams {
  circleDescription: string;
  contentInspiration: string;
  pdfIds?: string[];
  challengeCount?: number;
  responseId?: string;
}

export interface GeneratePlanMetaData {
  circleDescription: string;
  contentInspiration: string;
  pdfIds?: string[];
  challengeCount?: number;
  responseId?: string;
  loadedChallengesCount?: number;
}

export interface GeneratedPlan {
  _id: string;
  createdAt: string;
  updatedAt: string;
  user_id: string;
  title: string;
  instruction: string;
  keyTopics: Topic[];
  challenges: Challenge[];
  metadata?: GeneratePlanMetaData;
  isEdited?: boolean;
  deleted?: boolean;
}

export interface FilteredGeneratedContent {
  result: GeneratedScript[] | GeneratedPlan[];
  pagination: Pagination;
}

export const generatedContentApi = {
  generateScript: async (
    params: GenerateScriptParams
  ): Promise<GeneratedScript> => {
    const response = await api.post(`/generated-content/scripts`, {
      metadata: params,
    });
    return response.data.result;
  },

  generateCircleContentPlan: async (
    params: GeneratePlanParams
  ): Promise<GeneratedPlan> => {
    const response = await api.post(`/generated-content/circle-plans`, {
      metadata: params,
    });
    return response.data.result;
  },

  generateMoreChallenges: async ({
    planId,
    previous_response_id,
  }: {
    planId: string;
    previous_response_id: string;
  }): Promise<GeneratedPlan> => {
    const response = await api.post(
      `/generated-content/circle-plans/${planId}/more-challenges`,
      {
        previous_response_id,
      }
    );
    return response.data.result;
  },

  uploadPdf: async (
    formData: any
  ): Promise<{ fileId: string; filename: string }[]> => {
    const response = await api.post(
      '/generated-content/circle-plans/upload-pdf',
      formData
    );
    return response.data.result;
  },

  updateGeneratedScript: async ({
    scriptId,
    content,
    title,
  }: {
    scriptId: string;
    content: string;
    title: string;
  }): Promise<GeneratedScript> => {
    const response = await api.patch(`/generated-content/scripts/${scriptId}`, {
      content,
      title,
    });
    return response.data.result;
  },

  updateGeneratedPlan: async ({
    planId,
    challenges,
  }: {
    planId: string;
    challenges: Challenge[];
  }): Promise<GeneratedPlan> => {
    const response = await api.patch(
      `/generated-content/circle-plans/${planId}`,
      {
        challenges,
      }
    );
    return response.data.result;
  },

  getAllGeneratedContent: async ({
    page = 1,
    pageSize = 10,
    searchTerm,
    contentType,
  }: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    contentType?: ContentType;
  }): Promise<FilteredGeneratedContent> => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());
    if (searchTerm) {
      queryParams.append('searchTerm', searchTerm);
    }
    if (contentType) {
      queryParams.append('contentType', contentType);
    } else {
      queryParams.append('contentType', ContentType.CIRCLE_PLAN);
    }

    const response = await api.get(
      `/generated-content?${queryParams.toString()}`
    );
    return response.data;
  },

  getScriptsByPlanId: async ({
    planId,
    page = 1,
    pageSize = 10,
  }: {
    planId: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    result: GeneratedScript[];
    pagination: Pagination;
  }> => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());
    const response = await api.get(
      `/generated-content/circle-plans/${planId}/scripts?${queryParams.toString()}`
    );
    return response.data;
  },

  getScriptById: async (scriptId: string): Promise<GeneratedScript> => {
    const response = await api.get(`/generated-content/scripts/${scriptId}`);
    return response.data.result;
  },

  getPlanById: async (planId: string): Promise<GeneratedPlan> => {
    const response = await api.get(`/generated-content/circle-plans/${planId}`);
    return response.data.result;
  },

  deleteGeneratedScript: async ({
    scriptId,
  }: {
    scriptId: string;
  }): Promise<void> => {
    await api.delete(`/generated-content/scripts/${scriptId}`);
  },

  deleteGeneratedPlan: async ({
    planId,
  }: {
    planId: string;
  }): Promise<void> => {
    await api.delete(`/generated-content/circle-plans/${planId}`);
  },
};
